import { FormArray } from '@angular/forms';
import { BehaviorSubject, Observable } from 'rxjs';
import { DynamicFormControlType } from '../models/dynamic-form-group-field';
import { DynamicFormGroup } from './dynamic-form-group';
import { buildDynamicFormGroup } from './build-controls';
import { DynamicFormControl } from './dynamic-form-control';
import { validatorsOfType } from './build-validators';
export class DynamicFormArray extends FormArray {
  constructor(dfb, fieldDefinition, options) {
    super(fieldDefinition.data instanceof Observable ? null : fieldDefinition.data, validatorsOfType(fieldDefinition.validationFunctions, 'sync'), validatorsOfType(fieldDefinition.validationFunctions, 'async'));
    this.dfb = dfb;
    this.fieldDefinition = fieldDefinition;
    this.options = options;
    this._controls$ = new BehaviorSubject([]);
    this.controls$ = this._controls$.asObservable();
    if (!this.isPrimitive()) {
      if (!fieldDefinition.meta.classType) throw new Error('DynamicFormArray set to use non-primitive mode but received no classType meta info!');
      this.factoryModel = fieldDefinition.meta.classType;
    }
    this.controlName = fieldDefinition.name;
    this.validationDefinitions = fieldDefinition.validationDefinitions;
    this.formControlDiff = dfb.diffFactory.find([]).create(null);
  }
  addItem(value, options = {
    emitEvent: true
  }) {
    const isPrimitiveArray = this.fieldDefinition.controlType === DynamicFormControlType.primitiveArray;
    if (!isPrimitiveArray) {
      if (!Object.keys(value).length) {
        throw new Error('DynamicFormArray::AddItem error: received object value is empty. The passed in value must contain keys for each FormControl to create!');
      }
    }
    const control = this.createItem(value, this.isPrimitive());
    this.push(control);
  }
  isPrimitive() {
    return this.fieldDefinition.controlType === DynamicFormControlType.primitiveArray;
  }
  createItem(value, isPrimitiveArray) {
    let control;
    if (!isPrimitiveArray) {
      if (!value) value = {};
      control = buildDynamicFormGroup(this.dfb, this.fieldDefinition, value, this.options);
    } else {
      control = new DynamicFormControl({
        ...this.fieldDefinition,
        data: value
      });
    }
    return control;
    // push already triggers updateValueAndValidity
  }
  // WARNING: There's a buggy behavior I cannot figure out. calling setValue will always emit an event, even if options are configured otherwise.
  setValue(arrayData, options = {}, allowPatch = false) {
    if (allowPatch && arrayData.length === this.length) {
      return this.patchValue(arrayData, options);
    }
    const isPrimitive = this.isPrimitive();
    this.clear(options);
    arrayData.forEach(value => {
      const control = this.createItem(value, isPrimitive);
      this.push(control, options);
    });
    if (!isPrimitive) {
      for (let i = 0; i < arrayData.length; i++) {
        const controlValue = arrayData[i];
        this.controls[i].setObject(controlValue || {}, options);
      }
    }
    this.updateValueAndValidity(options);
  }
  getValue() {
    const list = [];
    for (let i = 0; i < this.controls.length; i++) {
      let value;
      if (this.controls[i] instanceof DynamicFormGroup) {
        value = this.controls[i].getObject();
      } else {
        value = this.controls[i].value;
      }
      list.push(value);
    }
    return list;
  }
  // NOTE: patchValue needs no override, as its compatible - https://github.com/angular/angular/blob/main/packages/forms/src/model/form_array.ts#L347
  // NOTE: reset needs no override, as its compatible - https://github.com/angular/angular/blob/main/packages/forms/src/model/form_array.ts#L414
  clear(options = {}) {
    if (this.controls.length < 1) return;
    while (this.length !== 0) {
      if (this.controls[0] instanceof DynamicFormGroup && this.controls[0].valueChangesSubscription) {
        this.controls[0].valueChangesSubscription.unsubscribe();
      }
      this.removeAt(0, options);
    }
    this.updateValueAndValidity(options);
  }
  updateValueAndValidity(opts = {}) {
    super.updateValueAndValidity(opts);
    // a little quirk to work around: updateValueAndValidity is being called synchronously in parent constructor.
    // bc of this, updateValueAndValidity will be called once with this.formControlDiff being unavailable.
    if (!this.formControlDiff) return;
    // using IterableDiffers to detect if anything changed actually.
    const changes = this.formControlDiff.diff(this.controls);
    if (changes) {
      // this log can show the unusual behavior spotted on page load, described in #338775b
      // console.log(">>>>>>> changes",changes);
      this._controls$.next(
      // this is extremely important! A NEW array must be emitted!
      // otherwise, regular change detection will not realize something has changed!
      [...this.controls]);
    }
  }
}